// Client-side CSV export helper.
// Generates an RFC-4180-escaped CSV (with UTF-8 BOM so Excel renders unicode
// and ₹ correctly) from already-fetched JSON rows and triggers a download.

export interface CsvColumn<T> {
  /** Header label shown in the CSV */
  header: string;
  /** Accessor: key on the row, or a function returning the cell value */
  accessor: keyof T | ((row: T) => unknown);
}

function escapeCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  // Quote if it contains comma, quote, or newline; double-up embedded quotes.
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function downloadCSV<T>(
  filename: string,
  columns: CsvColumn<T>[],
  rows: T[],
): void {
  const header = columns.map(c => escapeCell(c.header)).join(',');
  const body = rows.map(row =>
    columns
      .map(c => {
        const raw = typeof c.accessor === 'function'
          ? c.accessor(row)
          : (row as Record<string, unknown>)[c.accessor as string];
        return escapeCell(raw);
      })
      .join(',')
  );

  const csv = '﻿' + [header, ...body].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
